import React from "react";
import { Pencil, Trash2 } from "lucide-react";

export interface ChartTooltipItem {
  color: string;
  label: string;
  value: string | number;
}

export interface ChartHitoItem {
  id?: string;
  title: string;
  date?: string;
  comment?: string;
  category?: string;
  impact?: string;
}

export interface ChartTooltipProps {
  visible: boolean;
  x: number;
  y: number;
  title: string;
  items: ChartTooltipItem[];
  hitos?: ChartHitoItem[];
  containerWidth?: number;
  containerHeight?: number;
  canManageHitos?: boolean;
  isPinned?: boolean;
  onClose?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onEditHito?: (hito: ChartHitoItem) => void;
  onDeleteHito?: (hito: ChartHitoItem) => void;
}

export const ChartTooltip: React.FC<ChartTooltipProps> = ({
  visible,
  x,
  y,
  title,
  items,
  hitos = [],
  containerWidth = 800,
  containerHeight = 300,
  canManageHitos = false,
  isPinned = false,
  onClose,
  onMouseEnter,
  onMouseLeave,
  onEditHito,
  onDeleteHito,
}) => {
  if (!visible) return null;

  // Ancho fijo estricto: evita que se apriete o provoque reflow
  const tooltipWidth = 280;
  const isRightSide = x > containerWidth - tooltipWidth - 25;
  const leftPos = isRightSide
    ? Math.max(10, x - tooltipWidth - 14)
    : Math.min(x + 14, Math.max(10, containerWidth - tooltipWidth - 10));
  const topPos = Math.max(8, Math.min(y - 20, Math.max(8, (containerHeight || 250) - 180)));

  const hasInteractiveActions = Boolean((canManageHitos && hitos && hitos.length > 0) || isPinned);

  return (
    <div
      className="chart-floating-tooltip"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        position: "absolute",
        left: `${leftPos}px`,
        top: `${topPos}px`,
        width: `${tooltipWidth}px`,
        minWidth: `${tooltipWidth}px`,
        maxWidth: `${tooltipWidth}px`,
        boxSizing: "border-box",
        pointerEvents: hasInteractiveActions ? "auto" : "none",
        zIndex: 100,
        boxShadow: isPinned ? "0 10px 28px rgba(0,0,0,0.6), 0 0 0 1.5px #7d96ff" : undefined,
      }}
    >
      <div className="chart-tooltip-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span>{title}</span>
          {isPinned && (
            <span style={{ fontSize: "10px", background: "rgba(125, 150, 255, 0.2)", color: "#7d96ff", padding: "1px 6px", borderRadius: "4px", fontWeight: 600 }}>
              Fijado
            </span>
          )}
        </span>
        {isPinned && onClose && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            style={{
              background: "transparent",
              border: "none",
              color: "var(--muted, #8e96a8)",
              cursor: "pointer",
              fontSize: "14px",
              padding: "0 2px",
              lineHeight: 1,
            }}
            title="Cerrar / Desfijar"
          >
            ✕
          </button>
        )}
      </div>

      <div className="chart-tooltip-items">
        {items.map((item, idx) => (
          <div key={`${item.label}-${idx}`} className="chart-tooltip-row">
            <div className="chart-tooltip-label">
              <span
                className="chart-tooltip-dot"
                style={{ backgroundColor: item.color }}
              />
              <span>{item.label}</span>
            </div>
            <span className="chart-tooltip-value">{item.value}</span>
          </div>
        ))}
      </div>

      {hitos && hitos.length > 0 && (
        <div className="chart-tooltip-hitos-wrap">
          {hitos.map((h, idx) => (
            <div key={h.id || idx} className="chart-tooltip-hito-card">
              <div className="chart-tooltip-hito-badge">
                <span>🚩 Hito de ejecución</span>
                {h.category && <span className="hito-category-tag">{h.category}</span>}
              </div>
              <div className="chart-tooltip-hito-title">{h.title}</div>
              {h.comment && (
                <div className="chart-tooltip-hito-comment">{h.comment}</div>
              )}
              {canManageHitos && (
                <div className="chart-tooltip-hito-actions">
                  {onEditHito && (
                    <button
                      type="button"
                      className="chart-tooltip-action-btn edit"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditHito(h);
                      }}
                      title="Editar este hito"
                    >
                      <Pencil size={11} />
                      <span>Editar</span>
                    </button>
                  )}
                  {onDeleteHito && (
                    <button
                      type="button"
                      className="chart-tooltip-action-btn delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteHito(h);
                      }}
                      title="Eliminar este hito"
                    >
                      <Trash2 size={11} />
                      <span>Eliminar</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
